/* Sync - RfqJobQuoteDetailsHeader */

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getCurrentPageJobQuoteState,
    getQtyOrderedByJobQuoteLineId,
} from "@insite/client-framework/Store/Data/JobQuotes/JobQuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import generateOrder from "@insite/client-framework/Store/Pages/RfqJobQuoteDetails/Handlers/GenerateOrder";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import OrderActionSet, {
    orderActionSetFieldDefinitions,
    OrderActionSetPresentationProps,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    showGenerate = "showGenerate",
    showPrint = "showPrint",
}

interface OwnProps extends WidgetProps, ExpandStyles<RfqJobQuoteDetailsActionsStyles> {
    fields: {
        [fields.showGenerate]: boolean;
        [fields.showPrint]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState) => ({
    checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
    jobQuoteState: getCurrentPageJobQuoteState(state),
    qtyOrderedByJobQuoteLineId: getQtyOrderedByJobQuoteLineId(state),
});

const mapDispatchToProps = {
    generateOrder,
};

export interface RfqJobQuoteDetailsActionsStyles {
    actionSet?: OrderActionSetPresentationProps;
    generateOrderButton?: ButtonPresentationProps;
    generateOrderClickable?: LinkPresentationProps;
    printButton?: ButtonPresentationProps;
    printClickable?: LinkPresentationProps;
}

export const rfqJobQuoteDetailsActionsStyles: RfqJobQuoteDetailsActionsStyles = {
    printButton: { variant: "tertiary" },
};

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RfqJobQuoteDetailsActions = ({
    checkoutShippingPageUrl,
    fields: widgetFields,
    generateOrder,
    history,
    jobQuoteState,
    qtyOrderedByJobQuoteLineId,
    extendedStyles,
    overridedStyles,
}: Props) => {
    const styles = useExpandProps(rfqJobQuoteDetailsActionsStyles, extendedStyles, overridedStyles);

    if (!jobQuoteState.value) {
        return null;
    }

    const jobQuote = jobQuoteState.value;

    const generateOrderClickHandler = () => {
        generateOrder({
            jobQuote,
            onSuccess: result => {
                redirectToCheckout(result.id);
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    };

    const redirectToCheckout = (cartId: string) => {
        if (checkoutShippingPageUrl) {
            history.push(`${checkoutShippingPageUrl}?cartId=${cartId}`);
        }
    };

    const expirationDateIsGreaterThanCurrentDate = () => {
        if (!jobQuote || !jobQuote.expirationDate) {
            return true;
        }

        const expirationDate = jobQuote.expirationDate;
        const currentDate = new Date();

        expirationDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);

        return expirationDate >= currentDate;
    };

    const hasLineWithQuantity = () => {
        return Object.keys(qtyOrderedByJobQuoteLineId).some(key => {
            const qty = qtyOrderedByJobQuoteLineId[key];
            return qty !== undefined && qty > 0;
        });
    };

    const hasLineWithLimitExceeding = () => {
        return jobQuote.jobQuoteLineCollection?.some(jobQuoteLine => {
            const qty = qtyOrderedByJobQuoteLineId[jobQuoteLine.id];
            const qtyRemaining = jobQuoteLine.qtyOrdered! - (jobQuoteLine.qtySold || 0);
            return qty !== undefined && qty > 0 && qty > qtyRemaining;
        });
    };

    const generateOrderLabel = translate("Generate Order");

    return (
        <OrderActionSet
            {...styles.actionSet}
            fields={widgetFields}
            actions={[
                {
                    key: "generate",
                    label: generateOrderLabel,
                    onClick: generateOrderClickHandler,
                    buttonStyles: styles.generateOrderButton,
                    linkStyles: styles.generateOrderClickable,
                    testSelector: "rfqquote_generateorder",
                    isDisabled:
                        !expirationDateIsGreaterThanCurrentDate() ||
                        !hasLineWithQuantity() ||
                        hasLineWithLimitExceeding(),
                    isVisible: jobQuote.isEditable && !jobQuote.failedToGetRealTimeInventory,
                    field: fields.showGenerate,
                },
                {
                    preset: "print",
                    buttonStyles: styles.printButton,
                    linkStyles: styles.printClickable,
                    field: fields.showPrint,
                },
            ]}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(RfqJobQuoteDetailsActions)),
    definition: {
        group: "RFQ Job Quote Details",
        allowedContexts: ["RfqJobQuoteDetailsPage"],
        displayName: "Actions",
        fieldDefinitions: orderActionSetFieldDefinitions([
            { name: fields.showPrint },
            { name: fields.showGenerate, defaultState: "sticky" },
        ]),
    },
};

export default widgetModule;
